<template>
  <div>
    <file-list-dialog
      dialog-title="Anexos"
      :is-file-list-dialog-open.sync="isFileListDialogOpen"
      :files="anexos"
      no-files-message="Nenhum anexo disponível"
    />

    <encaminhar-ticket-dialog
      :is-encaminhar-ticket-dialog-open.sync="isEncaminharTicketDialogOpen"
      :ticket-id="ticketData.id"
      @refetch-data="getTicketsAndSelectFirst"
    />

    <salvar-ticket-dialog
      :is-salvar-ticket-dialog-open.sync="isSalvarTicketDialogOpen"
      :ticket-data="ticketData"
      :formularios-respostas="formulariosRespostas"
      :get-tickets="getTickets"
      :get-tickets-and-select-first="getTicketsAndSelectFirst"
      :ticket-esta-finalizado="ticketEstaFinalizado"
      :ticket-esta-resolvido="ticketEstaResolvido"
      @reset-ticket-data-and-get-mensagens="resetTicketDataEGetMensagens"
    />

    <v-card>
      <v-card-text>
        <v-row class="d-flex align-center">
          <v-col cols="auto">
            <span class="font-weight-bold">Status:
              <v-chip>
                {{ ticketData.statusNome }}
              </v-chip>
            </span>
          </v-col>

          <v-col cols="auto">
            <span class="font-weight-bold">Abertura:
              <v-chip>
                {{ functions.converterDateTimeStringEmLocaleDateTimeString(ticketData.dataCadastro) }}
              </v-chip>
            </span>
          </v-col>

          <v-col
            v-if="slas.length"
            cols="auto"
            class="d-flex align-center"
            style="gap: 8px"
          >
            <sla-countdown
              v-for="sla in slas"
              :key="sla.tipoSlaId"
              :sla="sla"
            />
          </v-col>

          <v-col cols="auto">
            <v-select
              v-model="tipoSolicitacaoIdLocale"
              dense
              outlined
              hide-details
              item-value="tipoSolicitacaoId"
              item-text="tipoSolicitacaoNome"
              label="Tipo de solicitação"
              no-data-text="Nenhum tipo de solicitação disponível"
              :filled="ticketEstaFinalizado"
              :readonly="ticketEstaFinalizado"
              :items="tiposDeSolicitacoes"
              @change="handleSelectTipoDeSolicitacaoChange"
            />
          </v-col>

          <v-col cols="auto">
            <v-btn
              v-if="$vuetify.breakpoint.mdAndUp"
              color="primary"
              outlined
              @click="isFileListDialogOpen = !isFileListDialogOpen"
            >
              <v-icon left>
                {{ icons.mdiLinkVariant }}
              </v-icon>

              <span>Anexos</span>
            </v-btn>

            <v-tooltip
              v-else
              top
              color="primary"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="isFileListDialogOpen = !isFileListDialogOpen"
                >
                  <v-icon>{{ icons.mdiLinkVariant }}</v-icon>
                </v-btn>
              </template>

              <span>Anexos</span>
            </v-tooltip>
          </v-col>

          <v-col cols="auto">
            <v-btn
              v-if="$vuetify.breakpoint.mdAndUp"
              color="primary"
              outlined
              :disabled="ticketEstaFinalizado"
              @click="isEncaminharTicketDialogOpen = !isEncaminharTicketDialogOpen"
            >
              <v-icon left>
                {{ icons.mdiShare }}
              </v-icon>

              <span>Encaminhar</span>
            </v-btn>

            <v-tooltip
              v-else
              top
              color="primary"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  :disabled="ticketEstaFinalizado"
                  v-bind="attrs"
                  v-on="on"
                  @click="isEncaminharTicketDialogOpen = !isEncaminharTicketDialogOpen"
                >
                  <v-icon>{{ icons.mdiShare }}</v-icon>
                </v-btn>
              </template>

              <span>Encaminhar</span>
            </v-tooltip>
          </v-col>

          <v-col cols="auto">
            <v-btn
              v-if="$vuetify.breakpoint.mdAndUp"
              color="primary"
              outlined
              :disabled="ticketEstaFinalizado"
              @click="isSalvarTicketDialogOpen = !isSalvarTicketDialogOpen"
            >
              <v-icon left>
                {{ icons.mdiContentSave }}
              </v-icon>

              <span>Salvar</span>
            </v-btn>

            <v-tooltip
              v-else
              top
              color="primary"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  :disabled="ticketEstaFinalizado"
                  v-bind="attrs"
                  v-on="on"
                  @click="isSalvarTicketDialogOpen = !isSalvarTicketDialogOpen"
                >
                  <v-icon>{{ icons.mdiContentSave }}</v-icon>
                </v-btn>
              </template>

              <span>Salvar</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect } from '@vue/composition-api'
import { mdiShare, mdiContentSave, mdiLinkVariant, mdiCalendarCheck } from '@mdi/js'
import { required, select } from '@core/utils/validation'
import FileListDialog from '@/components/file-list-dialog/FileListDialog.vue'
import EncaminharTicketDialog from './EncaminharTicketDialog.vue'
import SalvarTicketDialog from './SalvarTicketDialog.vue'
import SlaCountdown from '@/components/sla-badge/SlaCountdown.vue'
import TicketService from '@/services/modules/TicketService'
import useTicketSlas from '@/composables/useTicketSlas'
import { converterDateTimeStringEmLocaleDateTimeString } from '@/utils/dateTime'

export default {
  components: {
    FileListDialog,
    EncaminharTicketDialog,
    SalvarTicketDialog,
    SlaCountdown,
  },

  props: {
    ticketData: {
      type: Object,
      required: true,
    },
    anexos: {
      type: Array,
      required: true,
    },
    formulariosRespostas: {
      type: Array,
      required: true,
    },
    getTickets: {
      type: Function,
      required: true,
    },
    getTicketsAndSelectFirst: {
      type: Function,
      required: true,
    },
    atualizarTipoDeSolicitacao: {
      type: Function,
      required: true,
    },
    resetTicketDataEGetMensagens: {
      type: Function,
      required: true,
    },
    ticketEstaFinalizado: {
      type: Boolean,
      required: true,
    },
    ticketEstaResolvido: {
      type: Boolean,
      required: true,
    },
  },

  setup(props) {
    const tiposDeSolicitacoes = ref([])
    const tipoSolicitacaoIdLocale = ref(props.ticketData.tipoSolicitacaoId)

    watchEffect(() => {
      tipoSolicitacaoIdLocale.value = props.ticketData.tipoSolicitacaoId
    })

    function handleSelectTipoDeSolicitacaoChange() {
      props.atualizarTipoDeSolicitacao(tipoSolicitacaoIdLocale.value)
    }

    async function getTiposDeSolicitacoes(filaId) {
      if (!filaId) return
      tiposDeSolicitacoes.value = await TicketService.getTiposDeSolicitacoes(filaId)
    }
    watch(
      () => props.ticketData.filaId,
      (current, previous) => {
        if (current !== previous) {
          getTiposDeSolicitacoes(current)
        }
      },
    )

    const isFileListDialogOpen = ref(false)
    const isEncaminharTicketDialogOpen = ref(false)
    const isSalvarTicketDialogOpen = ref(false)

    const { slas } = useTicketSlas(computed(() => props.ticketData.id))

    return {
      tipoSolicitacaoIdLocale,
      tiposDeSolicitacoes,
      handleSelectTipoDeSolicitacaoChange,
      isFileListDialogOpen,
      isEncaminharTicketDialogOpen,
      isSalvarTicketDialogOpen,
      slas,
      functions: {
        converterDateTimeStringEmLocaleDateTimeString,
      },
      validators: {
        required,
        select,
      },
      icons: {
        mdiShare,
        mdiContentSave,
        mdiLinkVariant,
        mdiCalendarCheck,
      },
    }
  },
}
</script>
